import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNoIndex } from "@/hooks/useNoIndex";
import { RichiestaCard } from "@/components/RichiestaCard";
import { LOGO_URL } from "@/data/sponsors";

const FILTRI = [
  { key: "tutte", label: "Tutte" },
  { key: "storia", label: "Storie" },
  { key: "sponsor", label: "Sponsor" },
];

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function AdminRichieste() {
  useNoIndex("Bacheca richieste — DonnaImpresa");
  const navigate = useNavigate();
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("tutte");
  const [query, setQuery] = useState("");

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications");
      setRichieste(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin", { replace: true });
        return;
      }
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    carica();
  }, [carica]);

  const contatori = useMemo(
    () => ({
      totale: richieste.length,
      nuove: richieste.filter((r) => r.stato === "nuova").length,
      lavorazione: richieste.filter((r) => r.stato === "in lavorazione").length,
      chiuse: richieste.filter((r) => r.stato === "chiusa").length,
    }),
    [richieste]
  );

  const visibili = useMemo(() => {
    const q = query.trim().toLowerCase();
    return richieste.filter((r) => {
      if (filtro !== "tutte" && r.tipo !== filtro) return false;
      if (!q) return true;
      return [r.nome, r.email, r.azienda, r.settore, r.messaggio]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [richieste, filtro, query]);

  const cambiaStato = async (r, stato) => {
    try {
      const { data } = await api.patch(`/applications/${r.id}?stato=${encodeURIComponent(stato)}`);
      setRichieste((prev) => prev.map((x) => (x.id === r.id ? data : x)));
      toast.success(`Richiesta di ${r.nome}: ${stato}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare definitivamente la richiesta di ${r.nome}?`)) return;
    try {
      await api.delete(`/applications/${r.id}`);
      setRichieste((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Richiesta eliminata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const esporta = () => {
    const intestazioni = [
      "Data",
      "Tipo",
      "Stato",
      "Nome",
      "Azienda",
      "Settore",
      "Email",
      "Telefono",
      "Messaggio",
    ];
    const righe = visibili.map((r) =>
      [
        new Date(r.created_at).toLocaleString("it-IT"),
        r.tipo,
        r.stato,
        r.nome,
        r.azienda,
        r.settore,
        r.email,
        r.telefono,
        r.messaggio,
      ]
        .map(csvCell)
        .join(";")
    );
    const csv = "\uFEFF" + [intestazioni.join(";"), ...righe].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `richieste-donnaimpresa-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const esci = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-richieste-page">
      <header className="border-b border-[#E8E2E4] bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DonnaImpresa" className="h-10 w-10 rounded-xl object-cover" />
            <div>
              <p className="font-display text-lg leading-tight text-[#1A1617]">Bacheca richieste</p>
              <p className="text-xs text-[#9B8F92]">Area riservata DonnaImpresa</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={carica}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E2E4] px-4 py-2.5 text-xs font-semibold text-[#6B5E62] transition-colors duration-300 hover:border-[#E0708A] hover:text-[#E0708A]"
            >
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button
              onClick={esporta}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 rounded-full border border-[#E8E2E4] px-4 py-2.5 text-xs font-semibold text-[#6B5E62] transition-colors duration-300 hover:border-[#E0708A] hover:text-[#E0708A]"
            >
              <Download size={14} /> Esporta CSV
            </button>
            <button
              onClick={esci}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-[#5E2A52] px-4 py-2.5 text-xs font-semibold text-white transition-colors duration-300 hover:bg-[#E0708A]"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-10 sm:px-10">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="admin-contatori">
          {[
            ["Totale", contatori.totale, "totale"],
            ["Nuove", contatori.nuove, "nuove"],
            ["In lavorazione", contatori.lavorazione, "lavorazione"],
            ["Chiuse", contatori.chiuse, "chiuse"],
          ].map(([label, valore, key]) => (
            <div
              key={key}
              className="rounded-[22px] border border-[#E8E2E4] bg-white p-5"
              data-testid={`admin-contatore-${key}`}
            >
              <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9B8F92]">
                {label}
              </dt>
              <dd className="mt-2 font-display text-3xl text-[#5E2A52]">{valore}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            {FILTRI.map((f) => (
              <button
                key={f.key}
                onClick={() => setFiltro(f.key)}
                data-testid={`admin-filtro-${f.key}`}
                className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors duration-300 ${
                  filtro === f.key
                    ? "bg-[#5E2A52] text-white"
                    : "border border-[#E8E2E4] bg-white text-[#6B5E62] hover:border-[#E0708A] hover:text-[#E0708A]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9B8F92]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nome, email, azienda..."
              data-testid="admin-search"
              className="w-full rounded-full border border-[#E8E2E4] bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors duration-300 focus:border-[#E0708A]"
            />
          </div>
        </div>

        {loading ? (
          <div
            className="mt-16 flex items-center justify-center gap-3 text-sm text-[#6B5E62]"
            data-testid="admin-loading"
          >
            <Loader2 size={18} className="animate-spin text-[#E0708A]" /> Carico le richieste...
          </div>
        ) : visibili.length === 0 ? (
          <p
            className="mt-16 rounded-[24px] border border-dashed border-[#E8E2E4] p-12 text-center text-sm text-[#6B5E62]"
            data-testid="admin-empty"
          >
            Nessuna richiesta da mostrare con questi filtri.
          </p>
        ) : (
          <div className="mt-8 space-y-5" data-testid="admin-lista">
            {visibili.map((r) => (
              <RichiestaCard key={r.id} r={r} onStato={cambiaStato} onDelete={elimina} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
